/**
 * components/UploadGlobalConf/CategoryProcessor.js
 * Gestion du traitement des fichiers par catégorie - Version File System Access API
 */

import { readExcelFile, getSheetNames, getSheetColumnsPreview } from "../../utils/excelReader.js";
import { processTiroirsFile } from "../../utils/tiroirsConfProcessor.js";
import { processFiberCableFile } from "../../utils/fiberCableProcessor.js";
import { PVSuccessProcessor } from "../../utils/pvSuccessProcessor.js";
import { PVEchecProcessor } from "../../utils/pvEchecProcessor.js";
import { processBaseRopFile, analyzeBaseRopFile } from "../../utils/baseRopProcessor.js";

export class CategoryProcessor {
  constructor(parent) {
    this.parent = parent;
  }

  /**
   * Traiter un élément selon sa catégorie
   */
  async processItemByCategory(item, categoryKey) {
    switch (categoryKey) {
      case "configTiroirs":
        await this.processConfigTiroirs(item);
        break;

      case "baseROP":
        await this.processBaseROP(item);
        break;

      case "rapportFiberPvReussite":
      case "rapportFiberPvEchec":
        await this.processFiberCableReport(item, categoryKey);
        break;

      case "pvReussite":
      case "pvEchec":
      case "pvHs":
        await this.processSORFiles(item, categoryKey);
        break;

      default:
        throw new Error(`Catégorie ${categoryKey} non implémentée`);
    }
  }

  /**
   * Traiter Configuration des Tiroirs
   */
  async processConfigTiroirs(item) {
    const result = await processTiroirsFile(item.originalFile);

    if (!result.isValid) {
      throw new Error(result.errors.join("\n"));
    }

    // Marquer comme succès
    this.parent.assignedItems.set(item.name, {
      ...this.parent.assignedItems.get(item.name),
      status: "success",
      data: result.data,
    });

    this.parent.renderer.renderFileTree();
    this.parent.events.bindTreeEvents();

    // Notifier via callback
    if (this.parent.options.onDataLoaded) {
      this.parent.options.onDataLoaded("configTiroirs", result.data);
    }
  }

  /**
   * Traiter Configuration Base ROP (avec sous-nœuds)
   */
  async processBaseROP(item) {
    // Étape 1: Lire le fichier Excel
    const excelResult = await readExcelFile(item.originalFile);
    if (!excelResult.isValid) {
      throw new Error(excelResult.errors.join("\n"));
    }

    const workbook = excelResult.workbook;
    const availableSheets = getSheetNames(workbook);

    // Créer les sous-nœuds
    const assignedItem = this.parent.assignedItems.get(item.name);
    assignedItem.status = "pending_subnodes";
    assignedItem.workbook = workbook;
    assignedItem.availableSheets = availableSheets;
    assignedItem.subNodes = {
      sheet: { status: "pending", options: availableSheets, selected: null },
      column: { status: "disabled", options: [], selected: null },
    };

    this.parent.renderer.renderFileTree();
    this.parent.events.bindTreeEvents();
  }

  /**
   * Sélectionner une feuille pour BaseROP
   */
  async selectBaseRopSheet(itemName, sheetName) {
    const assignedItem = this.parent.assignedItems.get(itemName);
    const item = this.parent.uploadedFiles.find((f) => f.name === itemName);

    try {
      // Analyser la feuille
      const analysis = await analyzeBaseRopFile(item.originalFile, sheetName);
      if (!analysis.isValid) {
        throw new Error(analysis.errors.join("\n"));
      }

      // Obtenir les colonnes disponibles
      const availableColumns = getSheetColumnsPreview(assignedItem.workbook, sheetName, {
        headerRow: 2,
        previewRows: 20,
        maxColumns: 15,
      });

      if (availableColumns.length === 0) {
        throw new Error("Aucune colonne PBO trouvée");
      }

      // Mettre à jour les sous-nœuds
      assignedItem.selectedSheet = sheetName;
      assignedItem.detectedColumns = analysis.analysis.autoDetectedColumns;
      assignedItem.subNodes.sheet.status = "completed";
      assignedItem.subNodes.sheet.selected = sheetName;
      assignedItem.subNodes.column.status = "pending";
      assignedItem.subNodes.column.options = availableColumns;

      this.parent.renderer.renderFileTree();
      this.parent.events.bindTreeEvents();
    } catch (error) {
      assignedItem.subNodes.sheet.status = "error";
      assignedItem.subNodes.sheet.error = error.message;
      this.parent.renderer.renderFileTree();
      this.parent.events.bindTreeEvents();
      throw error;
    }
  }

  /**
   * Sélectionner une colonne pour BaseROP
   */
  async selectBaseRopColumn(itemName, column) {
    const assignedItem = this.parent.assignedItems.get(itemName);
    const item = this.parent.uploadedFiles.find((f) => f.name === itemName);

    try {
      // Traitement final
      const result = await processBaseRopFile(item.originalFile, assignedItem.selectedSheet, column);

      if (!result.isValid) {
        throw new Error(result.errors.join("\n"));
      }

      // Marquer comme succès complet
      assignedItem.status = "success";
      assignedItem.data = result.data;
      assignedItem.subNodes.column.status = "completed";
      assignedItem.subNodes.column.selected = column;

      // *** NOUVEAU : Enregistrer dans le store ***
      if (this.parent.state) {
        // Déterminer les colonnes depuis les données détectées
        const detectedColumns = assignedItem.detectedColumns || {};

        // Normaliser la colonne BPO pour avoir la même structure
        const normalizedBpoColumn = {
          index: column.index,
          letter: column.letter,
          name: column.name,
        };

        this.parent.state.setBaseRopWorkBookParams(
          assignedItem.workbook,
          assignedItem.selectedSheet,
          detectedColumns.REFERENCE, // referenceColumn
          detectedColumns.PORT, // portColumn
          normalizedBpoColumn, // bpoColumn (normalisé)
          item.originalFile
        );
      }

      this.parent.renderer.renderFileTree();
      this.parent.events.bindTreeEvents();

      // Notifier via callback
      if (this.parent.options.onDataLoaded) {
        this.parent.options.onDataLoaded("baseROP", result.data);
      }
    } catch (error) {
      assignedItem.subNodes.column.status = "error";
      assignedItem.subNodes.column.error = error.message;
      this.parent.renderer.renderFileTree();
      this.parent.events.bindTreeEvents();
      throw error;
    }
  }

  /**
   * Traiter Rapport Fiber Cable
   */
  async processFiberCableReport(item, categoryKey) {
    const result = await processFiberCableFile(item.originalFile);

    if (!result.isValid) {
      throw new Error(result.errors.join("\n"));
    }

    // Marquer comme succès
    this.parent.assignedItems.set(item.name, {
      ...this.parent.assignedItems.get(item.name),
      status: "success",
      data: result.data,
    });

    this.parent.renderer.renderFileTree();
    this.parent.events.bindTreeEvents();

    // Notifier via callback
    if (this.parent.options.onDataLoaded) {
      this.parent.options.onDataLoaded(categoryKey, result.data);
    }
  }

  /**
   * Traiter fichiers SOR - Version File System Access API
   */
  async processSORFiles(item, categoryKey) {
    try {
      let processResult;

      // Utiliser directement le directoryHandle disponible dans item
      if (categoryKey === "pvReussite") {
        processResult = await PVSuccessProcessor.processDirectory(item.directoryHandle);
      } else if (categoryKey === "pvEchec") {
        processResult = await PVEchecProcessor.processDirectory(item.directoryHandle);
      } else if (categoryKey === "pvHs") {
        processResult = await PVEchecProcessor.processDirectory(item.directoryHandle, { allowEmpty: true });
      }

      if (!processResult.isValid) {
        throw new Error(processResult.errors.join("\n"));
      }

      // Marquer comme succès
      this.parent.assignedItems.set(item.name, {
        ...this.parent.assignedItems.get(item.name),
        status: "success",
        data: processResult.data,
      });

      this.parent.renderer.renderFileTree();
      this.parent.events.bindTreeEvents();

      // Notifier via callback
      if (this.parent.options.onDataLoaded) {
        this.parent.options.onDataLoaded(categoryKey, processResult.data);
      }
    } catch (error) {
      this.parent.assignedItems.set(item.name, {
        ...this.parent.assignedItems.get(item.name),
        status: "error",
        error: error.message,
      });

      this.parent.renderer.renderFileTree();
      this.parent.events.bindTreeEvents();
      throw error;
    }
  }
}
